import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuthStore, useUIStore } from '../../store';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  LayoutDashboard, UtensilsCrossed, Table2, Users, ClipboardList,
  BarChart3, Settings, CreditCard, LogOut, Menu, X, Wine, Sun, Ticket
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/menu', label: 'Menu', icon: UtensilsCrossed },
  { path: '/admin/tables', label: 'Tables', icon: Table2 },
  { path: '/admin/happy-hours', label: 'Happy Hours', icon: Sun },
  { path: '/admin/promo-codes', label: 'Codes Promo', icon: Ticket },
  { path: '/admin/staff', label: 'Staff', icon: Users },
  { path: '/admin/orders', label: 'Commandes', icon: ClipboardList },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/settings', label: 'Paramètres', icon: Settings },
  { path: '/admin/billing', label: 'Abonnement', icon: CreditCard },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isAuthenticated } = useAuthStore();
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUIStore();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);

  useEffect(() => {
    if (!isAuthenticated() || !isAdmin()) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isAdmin, navigate]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [setSidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="admin-layout min-h-screen bg-[#F9FAFB]">
      {/* Mobile Header */}
      <header className="admin-mobile-header lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Wine className="w-6 h-6 text-[#7C3AED]" />
          <span className="font-bold" style={{ fontFamily: 'Manrope' }}>GlassOrder</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          aria-label={sidebarOpen ? 'Fermer la navigation' : 'Ouvrir la navigation'}
          aria-expanded={sidebarOpen}
          aria-controls="admin-navigation"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      <div className="flex">
        {/* Sidebar Overlay for Mobile */}
        <AnimatePresence>
          {sidebarOpen && isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.aside
              id="admin-navigation"
              initial={isMobile ? { x: -280 } : { opacity: 1 }}
              animate={isMobile ? { x: 0 } : { opacity: 1 }}
              exit={isMobile ? { x: -280 } : { opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`
                admin-sidebar fixed lg:sticky top-0 left-0 h-[100dvh] w-[min(280px,88vw)] lg:w-[280px] bg-white border-r border-gray-200
                flex flex-col z-50 lg:z-0
              `}
            >
              {/* Logo */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#7C3AED] rounded-xl flex items-center justify-center">
                    <Wine className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>
                      GlassOrder
                    </h1>
                    <p className="text-xs text-gray-400">Admin Panel</p>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <ScrollArea className="min-h-0 flex-1 py-4">
                <nav className="px-3 space-y-1">
                  {navItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavClick(item.path)}
                        data-testid={`nav-${item.path.split('/').pop()}`}
                        className={`
                          w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all
                          ${isActive 
                            ? 'bg-[#111827] text-white' 
                            : 'text-gray-600 hover:bg-gray-100'
                          }
                        `}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </ScrollArea>

              {/* User Section */}
              <div className="admin-sidebar-footer p-4 border-t border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-gray-600">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 truncate">{user?.name}</p>
                    <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleLogout}
                  data-testid="admin-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main className={`flex-1 min-h-screen min-w-0 ${sidebarOpen && !isMobile ? 'lg:ml-0' : ''}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
